import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ENV from '../../config/env';

// Define subscription plan types and pricing details
interface PlanOption {
  id: string;
  name: string;
  price: number;
  displayPrice?: string;
  currency: string;
  per: string;
  discount: number | null;
  monthlyEquivalent?: string;
  saveText: string | null;
}

const PricingSection: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string>('monthly'); // Default to monthly
  const [selectedTeamPlan, setSelectedTeamPlan] = useState<string>('monthly'); // Team plan billing cycle
  const isLoggedIn = localStorage.getItem('token');
  
  const planOptions: PlanOption[] = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: 19,
      currency: '£',
      per: 'month',
      discount: null,
      saveText: null
    },
    {
      id: 'annual',
      name: 'Annual',
      price: 182.40,  // £19 × 12 months with 20% discount = £19 × 12 × 0.8 = £182.40
      displayPrice: '182.40',
      monthlyEquivalent: '£15.20/mo',
      currency: '£',
      per: 'year',
      discount: 20,
      saveText: 'Save 20%'
    }
  ];

  // State for email input and validation
  const [email, setEmail] = useState<string>('');
  const [showEmailInput, setShowEmailInput] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  
  // Validate email format
  const validateEmail = (email: string): boolean => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  // Handler for Stripe checkout using guest checkout endpoint
  // State for telemarketing checkout
  const [teleEmail, setTeleEmail] = useState<string>('');
  const [showTeleEmailInput, setShowTeleEmailInput] = useState<string | null>(null); // stores planId or null
  const [teleEmailError, setTeleEmailError] = useState<string | null>(null);
  const [teleProcessing, setTeleProcessing] = useState<boolean>(false);

  // Handler for telemarketing plan checkout (no free trial)
  const handleTelemarketingCheckout = async (planId: string) => {
    const userEmail = localStorage.getItem('userEmail') || '';
    const token = localStorage.getItem('token');
    
    // If not logged in and no email provided, show email input
    if (!token && !userEmail) {
      if (showTeleEmailInput !== planId) {
        setShowTeleEmailInput(planId);
        setTeleEmailError(null);
        return;
      }
      
      // Validate email
      if (!teleEmail) {
        setTeleEmailError('Email is required');
        return;
      }
      if (!validateEmail(teleEmail)) {
        setTeleEmailError('Please enter a valid email address');
        return;
      }
    }
    
    const emailToUse = token ? userEmail : teleEmail;
    
    if (!emailToUse) {
      setTeleEmailError('Email is required');
      setShowTeleEmailInput(planId);
      return;
    }
    
    setTeleProcessing(true);
    setTeleEmailError(null);
    
    try {
      const apiUrl = `${ENV.API_URL}/api/payment/guest-checkout`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: emailToUse,
          planId: planId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Telemarketing Checkout API Error:', errorData);
        throw new Error(errorData.error || errorData.details || 'Failed to create checkout session');
      }
      
      const { url } = await response.json();
      window.location.href = url;
    } catch (error: any) {
      console.error('Error creating telemarketing checkout:', error);
      setTeleEmailError(error.message || 'Failed to start checkout. Please try again.');
      setTeleProcessing(false);
    }
  };

  const handleCheckout = async () => {
    const plan = planOptions.find(p => p.id === selectedPlan);
    if (!plan) return;
    
    // If user is not logged in, show email input first
    if (!isLoggedIn) {
      if (!showEmailInput) {
        setShowEmailInput(true);
        return;
      }
      
      // Validate email before proceeding
      if (!email) {
        setEmailError('Email is required');
        return;
      }
      
      if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      
      setEmailError(null);
      setIsProcessing(true);
      
      try {
        // Call the guest checkout endpoint
        const apiUrl = `${ENV.API_URL}/api/payment/guest-checkout`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email,
            planId: plan.id
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Checkout API Error:', errorData);
          throw new Error(errorData.error || errorData.details || 'Failed to create checkout session');
        }
        
        const { url } = await response.json();
        
        // Redirect to Stripe checkout
        window.location.href = url;
      } catch (error: any) {
        console.error('Error creating checkout session:', error);
        setEmailError(error.message || 'Failed to create checkout session. Please try again.');
        setIsProcessing(false);
      }
    } else {
      // User is logged in, use guest checkout with their email
      setIsProcessing(true);
      
      try {
        // Get user email from localStorage if available
        const userEmail = localStorage.getItem('userEmail') || '';
        
        // Call the guest checkout endpoint
        const apiUrl = `${ENV.API_URL}/api/payment/guest-checkout`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            email: userEmail, // This will be overridden by the server if empty
            planId: plan.id
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Checkout API Error:', errorData);
          throw new Error(errorData.error || errorData.details || 'Failed to create checkout session');
        }
        
        const { url } = await response.json();
        
        // Redirect to Stripe checkout
        window.location.href = url;
      } catch (error: any) {
        console.error('Error creating checkout session:', error);
        setEmailError(error.message || 'Failed to create checkout session. Please try again.');
        setIsProcessing(false);
      }
    }
  };

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background with subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-100 to-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-50 via-transparent to-transparent opacity-60"></div>
      </div>
      
      {/* Decorative elements */}
      <div className="absolute left-10 top-40 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
      <div className="absolute right-0 bottom-0 w-64 h-64 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
      
      <div className="container relative mx-auto px-4 z-10">
        {/* Section header removed to save space */}
        
        {/* 7-day free trial banner - moved above pricing card */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="bg-gradient-to-r from-blue-400 to-indigo-500 text-white rounded-xl shadow-lg p-6 relative z-10">
            <div className="absolute -top-4 -right-4 bg-yellow-500 text-blue-900 font-extrabold px-4 py-1 rounded-full shadow-md transform rotate-3">
              FREE
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-2">Try All 23 AI Modules Free for 7 Days</h3>
            <p className="text-lg mb-4">Experience the full power of an all-in-one business platform. See how much time and money you'll save!</p>
            <div className="bg-white rounded-lg p-3 shadow-inner">
              <p className="text-blue-800 font-medium">Your card will only be charged after your 7-day trial ends. Cancel anytime during trial period.</p>
            </div>
          </div>
        </div>
        
        {/* Pricing cards - Individual and Team */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Individual Plan Card */}
          <div className="flex flex-col">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 border-2 border-blue-500 h-full">
            {/* Card header */}
            <div className="bg-white px-6 py-8 text-center border-b border-gray-200">
              <div className="h-8 mb-4"></div> {/* Spacer to match Team card height */}
              <h3 className="text-2xl font-bold mb-1 text-blue-600">Everything Your Business Needs</h3>
              <p className="text-blue-500 mt-2 font-semibold">Replace 10+ subscriptions with one powerful platform</p>
            </div>
            
            {/* Plan selection toggle */}
            <div className="px-6 pt-8 pb-4">
              <div className="flex justify-center mb-8">
                <div className="flex p-1 rounded-xl bg-gray-200/50 backdrop-blur-sm">
                  <button 
                    className={`
                      px-6 py-3 text-sm font-medium rounded-lg transition-all duration-300
                      ${selectedPlan === 'monthly' 
                        ? 'bg-white text-blue-600 shadow-md' 
                        : 'text-gray-600 hover:bg-white/50'}
                    `}
                    onClick={() => setSelectedPlan('monthly')}
                  >
                    Monthly
                  </button>
                  <button 
                    className={`
                      px-6 py-3 text-sm font-medium rounded-lg transition-all duration-300
                      ${selectedPlan === 'annual' 
                        ? 'bg-white text-blue-600 shadow-md' 
                        : 'text-gray-600 hover:bg-white/50'}
                    `}
                    onClick={() => setSelectedPlan('annual')}
                  >
                    Annual
                  </button>
                </div>
              </div>
              
              <div className="text-center mb-8 px-4">
                {selectedPlan === 'monthly' ? (
                  <div className="flex flex-col items-center">
                    <div className="text-4xl font-bold text-gray-800 mb-1">£19<span className="text-xl text-gray-500">/month</span></div>
                    <div className="text-gray-500">Billed monthly</div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="text-4xl font-bold text-gray-800 mb-1">£182.40<span className="text-xl text-gray-500">/year</span></div>
                    <div className="text-blue-600 font-medium mb-1">Just £15.20/month, billed annually</div>
                    <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-bold text-sm">Save 20%</div>
                  </div>
                )}
              </div>
              
              {/* Features list */}
              <div className="mb-8">
                <div className="mb-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <p className="text-sm font-bold text-green-800 text-center">
                    💰 Save 70% vs buying tools separately • Replaces HubSpot + Asana + Zapier + BambooHR + 10 more
                  </p>
                </div>
                <h4 className="font-semibold text-gray-800 mb-4">All 23 AI-powered modules included:</h4>
                <ul className="space-y-3">
                  {[
                    'AI Business Consultant',
                    'AI Business Plan Generator',
                    'Brand Kit Creator',
                    'AI Customer CRM',
                    'AI LinkedIn Enhancer',
                    'AI Press Release Generator',
                    'AI Social Media Planner',
                    'AI Cold Email Engine',
                    'AI Affiliate Marketing Engine',
                    'AI Link Intelligence Suite',
                    'AI Finance Command Center',
                    'AI Pricing Intelligence Suite',
                    'AI Revenue Operations Hub',
                    'AI Supplier Operations Hub',
                    'AI Risk Management Command Center',
                    'AI Investor CRM & Fundraising Hub',
                    'AI Legal & HR Document Hub',
                    'AI Project Management Hub',
                    'AI Staff Management & HR Hub',
                    'AI Recruitment & ATS Platform',
                    'SEO Content Hub',
                    'SEO Tools Hub',
                    'Ecommerce Hub'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center text-slate-700">
                      <svg className="w-5 h-5 mr-3 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 font-medium">Plus 40+ additional tools including text-to-image, video generator, SEO analyzer, and more</p>
                </div>
              </div>
              
              {/* Email Input for Guest Users */}
              {showEmailInput && !isLoggedIn && (
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email address to create your account
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className={`w-full px-3 py-2 border ${emailError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    disabled={isProcessing}
                  />
                  {emailError && (
                    <p className="mt-1 text-sm text-red-600">{emailError}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    We'll use this to create your account after payment.
                  </p>
                </div>
              )}
              
              {/* Trial Message Banner */}
              <div className="bg-blue-50 border border-blue-200 text-blue-800 font-semibold p-3 rounded-lg mb-4">
                <div className="flex items-center">
                  <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-md mr-2">FREE</span>
                  <span>7-Day Free Trial - No Charge Until Day 8</span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleCheckout}
                disabled={isProcessing}
                className={`w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform transition-all hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isProcessing ? 'Processing...' : showEmailInput ? 'Provide Payment Details to Start Your Trial' : 'Start My 7-Day Free Trial'}
              </button>
              
              {/* Cancel anytime message */}
              <div className="text-center mt-3 text-sm text-slate-600">
                Cancel anytime during trial - no charge
              </div>
              
              {/* Stripe badge */}
              <div className="flex items-center justify-center mt-4 text-slate-500 text-sm">
                <span>Secure payment via <span className="font-medium text-blue-600 ml-1">Stripe</span></span>
              </div>
            </div>
            </div>
          </div>
          
          {/* Team Plan Card */}
          <div className="flex flex-col">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 border-2 border-indigo-500 h-full">
              {/* Popular badge */}
              <div className="bg-indigo-500 text-white text-center py-2 font-bold text-sm">
                🔥 MOST POPULAR FOR TEAMS
              </div>
              
              {/* Card header */}
              <div className="bg-white px-6 py-8 text-center border-b border-gray-200">
                <h3 className="text-2xl font-bold mb-1 text-indigo-600">Team Plan</h3>
                <p className="text-indigo-500 mt-2 font-semibold">Scale faster with collaborative AI tools</p>
              </div>
              
              {/* Pricing */}
              <div className="px-6 pt-8 pb-4">
                {/* Plan selection toggle */}
                <div className="flex justify-center mb-8">
                  <div className="flex p-1 rounded-xl bg-gray-200/50 backdrop-blur-sm">
                    <button 
                      className={`
                        px-6 py-3 text-sm font-medium rounded-lg transition-all duration-300
                        ${selectedTeamPlan === 'monthly' 
                          ? 'bg-white text-indigo-600 shadow-md' 
                          : 'text-gray-600 hover:bg-white/50'}
                      `}
                      onClick={() => setSelectedTeamPlan('monthly')}
                    >
                      Monthly
                    </button>
                    <button 
                      className={`
                        px-6 py-3 text-sm font-medium rounded-lg transition-all duration-300
                        ${selectedTeamPlan === 'annual' 
                          ? 'bg-white text-indigo-600 shadow-md' 
                          : 'text-gray-600 hover:bg-white/50'}
                      `}
                      onClick={() => setSelectedTeamPlan('annual')}
                    >
                      Annual
                    </button>
                  </div>
                </div>
                
                <div className="text-center mb-8 px-4">
                  {selectedTeamPlan === 'monthly' ? (
                    <div className="flex flex-col items-center">
                      <div className="text-4xl font-bold text-gray-800 mb-1">£49<span className="text-xl text-gray-500">/month</span></div>
                      <div className="text-gray-600 mb-2">For up to 3 users</div>
                      <div className="text-sm text-indigo-600 font-medium">+ £15/month per additional user</div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="text-4xl font-bold text-gray-800 mb-1">£490<span className="text-xl text-gray-500">/year</span></div>
                      <div className="text-indigo-600 font-medium mb-1">Just £40.83/month for 3 users</div>
                      <div className="text-gray-600 mb-2">Billed annually</div>
                      <div className="text-sm text-indigo-600 font-medium">+ £150/year per additional user</div>
                      <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-bold text-sm mt-2">Save 17%</div>
                    </div>
                  )}
                </div>
                
                {/* Features list */}
                <div className="mb-8">
                  <div className="mb-4 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                    <p className="text-sm font-bold text-indigo-800 text-center">
                      🚀 Just £16.33/user/month • Less than a single tool subscription per person
                    </p>
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-4">Everything in Premium, plus:</h4>
                  <ul className="space-y-3">
                    {[
                      'Up to 3 team members included',
                      'Shared workspace & projects',
                      'Team collaboration tools',
                      'Centralized billing',
                      'Admin user management',
                      'Priority support',
                      'Advanced permissions',
                      'Team analytics dashboard'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center text-slate-700">
                        <svg className="w-5 h-5 mr-3 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Trial Message Banner */}
                <div className="bg-indigo-50 border border-indigo-200 text-indigo-800 font-semibold p-3 rounded-lg mb-4">
                  <div className="flex items-center">
                    <span className="bg-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-md mr-2">FREE</span>
                    <span>7-Day Free Trial - No Charge Until Day 8</span>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => navigate(`/company-signup?billing=${selectedTeamPlan}`)}
                  className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform transition-all hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
                >
                  Start Team Trial {selectedTeamPlan === 'annual' ? '(Annual)' : '(Monthly)'}
                </button>
                
                {/* Cancel anytime message */}
                <div className="text-center mt-3 text-sm text-slate-600">
                  Cancel anytime during trial - no charge
                </div>
                
                {/* Stripe badge */}
                <div className="flex items-center justify-center mt-4 text-slate-500 text-sm">
                  <span>Secure payment via <span className="font-medium text-indigo-600 ml-1">Stripe</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* AI Telemarketing Plans Section */}
        <div className="mt-16">
          <div className="text-center mb-10">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full mb-4">
              <span className="text-purple-700 font-semibold">🤖 AI Cold Calling Agent</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">AI Telemarketing Plans</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Automate outbound calls with our AI-powered telemarketing agent. Scale your sales without scaling your team.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {/* Entry Plan */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-purple-200 hover:border-purple-400 transition-all overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-4">
                <h3 className="text-xl font-bold">Entry</h3>
                <p className="text-purple-100 text-sm">Perfect for getting started</p>
              </div>
              <div className="px-6 py-6">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-gray-900">£299<span className="text-lg text-gray-500">/mo</span></div>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>3 Additional Users</strong></span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>500 AI Minutes</strong>/month</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>All Explanatum Features</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Campaign Management</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Call Analytics</span>
                  </li>
                </ul>
                <div className="text-sm text-gray-500 mb-4 text-center">
                  Add more: +£299 (3 users + 500 min)
                </div>
                {showTeleEmailInput === 'entry_telemarketing' && (
                  <div className="mb-3">
                    <input
                      type="email"
                      value={teleEmail}
                      onChange={(e) => setTeleEmail(e.target.value)}
                      placeholder="Enter your email"
                      className={`w-full px-3 py-2 border ${teleEmailError ? 'border-red-500' : 'border-gray-300'} rounded-lg text-sm`}
                      disabled={teleProcessing}
                    />
                    {teleEmailError && <p className="text-red-500 text-xs mt-1">{teleEmailError}</p>}
                  </div>
                )}
                <button
                  onClick={() => handleTelemarketingCheckout('entry_telemarketing')}
                  disabled={teleProcessing}
                  className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {teleProcessing && showTeleEmailInput === 'entry_telemarketing' ? 'Processing...' : showTeleEmailInput === 'entry_telemarketing' ? 'Continue to Payment' : 'Buy Now'}
                </button>
              </div>
            </div>
            
            {/* Pro Plan */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-indigo-500 hover:border-indigo-600 transition-all overflow-hidden transform md:scale-105 relative">
              <div className="absolute top-0 left-0 right-0 bg-yellow-400 text-center py-1 text-xs font-bold text-gray-900">
                MOST POPULAR
              </div>
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-6 py-4 mt-6">
                <h3 className="text-xl font-bold">Pro</h3>
                <p className="text-indigo-100 text-sm">Best for growing teams</p>
              </div>
              <div className="px-6 py-6">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-gray-900">£899<span className="text-lg text-gray-500">/mo</span></div>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>3 Additional Users</strong></span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>2,000 AI Minutes</strong>/month</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>All Explanatum Features</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Priority Call Support</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Advanced Analytics</span>
                  </li>
                </ul>
                <div className="text-sm text-gray-500 mb-4 text-center">
                  Add more: +£799 (3 users + 2,000 min)
                </div>
                {showTeleEmailInput === 'pro_telemarketing' && (
                  <div className="mb-3">
                    <input
                      type="email"
                      value={teleEmail}
                      onChange={(e) => setTeleEmail(e.target.value)}
                      placeholder="Enter your email"
                      className={`w-full px-3 py-2 border ${teleEmailError ? 'border-red-500' : 'border-gray-300'} rounded-lg text-sm`}
                      disabled={teleProcessing}
                    />
                    {teleEmailError && <p className="text-red-500 text-xs mt-1">{teleEmailError}</p>}
                  </div>
                )}
                <button
                  onClick={() => handleTelemarketingCheckout('pro_telemarketing')}
                  disabled={teleProcessing}
                  className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {teleProcessing && showTeleEmailInput === 'pro_telemarketing' ? 'Processing...' : showTeleEmailInput === 'pro_telemarketing' ? 'Continue to Payment' : 'Buy Now'}
                </button>
              </div>
            </div>
            
            {/* Enterprise Plan */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-emerald-200 hover:border-emerald-400 transition-all overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-4">
                <h3 className="text-xl font-bold">Enterprise</h3>
                <p className="text-emerald-100 text-sm">For high-volume operations</p>
              </div>
              <div className="px-6 py-6">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-gray-900">£3,995<span className="text-lg text-gray-500">/mo</span></div>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>10 Additional Users</strong></span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span><strong>10,000 AI Minutes</strong>/month</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Dedicated Account Manager</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Unlimited Campaigns</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Custom Integrations</span>
                  </li>
                </ul>
                <div className="text-sm text-gray-500 mb-4 text-center">
                  Add more: +£3,495 (10 users + 10,000 min)
                </div>
                {showTeleEmailInput === 'enterprise_telemarketing' && (
                  <div className="mb-3">
                    <input
                      type="email"
                      value={teleEmail}
                      onChange={(e) => setTeleEmail(e.target.value)}
                      placeholder="Enter your email"
                      className={`w-full px-3 py-2 border ${teleEmailError ? 'border-red-500' : 'border-gray-300'} rounded-lg text-sm`}
                      disabled={teleProcessing}
                    />
                    {teleEmailError && <p className="text-red-500 text-xs mt-1">{teleEmailError}</p>}
                  </div>
                )}
                <button
                  onClick={() => handleTelemarketingCheckout('enterprise_telemarketing')}
                  disabled={teleProcessing}
                  className="w-full py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  {teleProcessing && showTeleEmailInput === 'enterprise_telemarketing' ? 'Processing...' : showTeleEmailInput === 'enterprise_telemarketing' ? 'Continue to Payment' : 'Buy Now'}
                </button>
              </div>
            </div>
          </div>
          
          {/* No free trial notice */}
          <div className="text-center mt-6 text-gray-600">
            <p className="text-sm">AI Telemarketing plans do not include a free trial due to telephony costs.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
